import { useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout.tsx";
import PageTitle from "@/components/PageTitle";
import {
  openProductQuickView,
  useProductQuickViewId,
} from "@/components/productQuickView/store";
import type { Market } from "@/lib/useMarket";
import MatrixScrollBox from "./components/MatrixScrollBox";
import StockMatrixGrid, { type WarehouseColumn } from "./components/StockMatrixGrid";
import StockMatrixToolbar from "./components/StockMatrixToolbar";
import ProductKindTabs from "./components/ProductKindTabs";
import GroupHeader from "./components/GroupHeader";
import FoldAllButtons from "./components/FoldAllButtons";
import AllMarketsOverview from "./components/AllMarketsOverview";
import CurrencyMenu from "@/pages/Revenue/components/CurrencyMenu";
import { healthColumns } from "./components/StockHealthCells";
import {
  sortRows,
  summarizeStock,
  type MatrixSort,
  type ProductKind,
} from "./lib/stockHealth";
import { groupProducts } from "./lib/groupProducts";
import { useStoredGroupBy } from "./lib/useStoredGroupBy";
import { filterProducts, type ProductFilters } from "./lib/productFilters";
import { useProductKindParam } from "./lib/useProductKindParam";
import { useCurrencyParam } from "./lib/useCurrencyParam";
import { addMarkets, convertForDisplay, storedValue, type AllMarketsRow } from "./lib/allMarketsTotals";

interface MarketSection extends Market {
  warehouses: WarehouseColumn[];
  products: AllMarketsRow[];
}

interface Props {
  // The market in the URL (/inventory/overview/:market_slug); null on the
  // all-markets URL.
  market: Market | null;
  // Every market, or only the one in the URL.
  markets: MarketSection[];
  // Whether this user may see cost-based figures (Value column and totals).
  value_visible: boolean;
  // The product group tree in display order, parents before children.
  product_groups: { id: string; path: string; depth: number }[];
  // The currency products.cogs is stored in; the value card's default.
  cogs_currency: string;
  // Currencies the value card can be shown in.
  currencies: string[];
  // USD-based rates for those currencies, for display-only conversion.
  rates: Record<string, number>;
}

const EMPTY = new Set<string>();

function toggled(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

// Inventory Overview for every market: one band per market, each holding
// that market's own table (its warehouses, its statuses, its currency). Bands
// start closed and show the market's totals; the cards above add the markets
// together. One toolbar and one set of tabs filter every band. Expand all /
// Collapse all open or close every market and every product group inside them.
// With one market picked there is no band: its table sits under the toolbar.
export default function AllMarketsByWarehouse({
  market, markets, value_visible, product_groups, cogs_currency, currencies, rates,
}: Props) {
  const quickViewId = useProductQuickViewId();
  const [filters, setFilters] = useState<ProductFilters>(() => ({
    query: "",
    groups: new Set(),
  }));
  const [kind, setKind] = useProductKindParam();
  const [groupBy, setGroupBy] = useStoredGroupBy();
  const [sort, setSort] = useState<MatrixSort>({
    column: "status",
    direction: "normal",
  });
  const [hiddenWarehouseIds, setHiddenWarehouseIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [openMarketIds, setOpenMarketIds] = useState<Set<string>>(() => new Set());
  // Closed product groups per market id; product groups start open.
  const [closedGroupKeys, setClosedGroupKeys] = useState<Record<string, Set<string>>>({});
  const [displayCurrency, setDisplayCurrency] = useCurrencyParam(currencies, market?.currency ?? cogs_currency);

  // The toolbar lists each warehouse once, even one allocated in two markets.
  const allWarehouses = useMemo(() => {
    const byId = new Map<string, WarehouseColumn>();
    for (const m of markets) for (const w of m.warehouses) if (!byId.has(w.external_id)) byId.set(w.external_id, w);
    return [...byId.values()];
  }, [markets]);
  const allProducts = useMemo(() => markets.flatMap((m) => m.products), [markets]);

  // Totals across markets: a product stocked in two markets counts in each.
  const kindCounts = useMemo(() => {
    const counts: Record<ProductKind, number> = { for_sale: 0, gift: 0, not_moving: 0 };
    for (const p of allProducts) counts[p.kind] += 1;
    return counts;
  }, [allProducts]);

  const sections = useMemo(
    () =>
      markets.map((market) => {
        const kindProducts = market.products.filter((p) => p.kind === kind);
        const filtered = filterProducts(kindProducts, filters);
        // Sorted before grouping, which keeps row order, so each group is sorted too.
        const sorted = sortRows(filtered, sort);
        const warehouses = market.warehouses.filter((w) => !hiddenWarehouseIds.has(w.external_id));
        return {
          market,
          warehouses,
          products: sorted,
          filtered: filtered !== kindProducts,
          shown: filtered,
          summary: summarizeStock(filtered, warehouses.map((w) => w.external_id)),
          groups: groupBy === "none" ? undefined : groupProducts(sorted, groupBy),
        };
      }),
    [markets, kind, filters, sort, hiddenWarehouseIds, groupBy],
  );

  const columns = healthColumns(value_visible);

  const overview = useMemo(() => addMarkets(sections.map((s) => s.summary)), [sections]);
  const value = useMemo(
    () =>
      convertForDisplay(
        storedValue(sections.map((s) => ({ rows: s.shown, warehouseIds: s.warehouses.map((w) => w.external_id) }))),
        cogs_currency,
        displayCurrency,
        rates,
      ),
    [sections, cogs_currency, displayCurrency, rates],
  );

  const expandAll = () => {
    setOpenMarketIds(new Set(markets.map((m) => m.id)));
    setClosedGroupKeys({});
  };
  const collapseAll = () => {
    setOpenMarketIds(new Set());
    setClosedGroupKeys(
      Object.fromEntries(sections.map((s) => [s.market.id, new Set((s.groups ?? []).map((g) => g.key))])),
    );
  };
  const toggleGroup = (marketId: string, key: string) =>
    setClosedGroupKeys((prev) => ({ ...prev, [marketId]: toggled(prev[marketId] ?? new Set(), key) }));

  return (
    <AppLayout flush>
      <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <MatrixScrollBox
          top={
            <>
              <div className="bg-page-background px-6 pb-3 pt-4">
                <PageTitle
                  title="Inventory Overview"
                  subtitle={market ? "Today's stock across all warehouses" : "Today's stock in every market. Each market in its own currency."}
                />
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <ProductKindTabs value={kind} onChange={setKind} counts={kindCounts} />
                  {value_visible && (
                    <CurrencyMenu currencies={currencies} value={displayCurrency} onChange={setDisplayCurrency} />
                  )}
                </div>
                <div className="mt-4">
                  <AllMarketsOverview
                    overview={overview}
                    value={value}
                    displayCurrency={displayCurrency}
                    cogsCurrency={cogs_currency}
                    rates={rates}
                    valueVisible={value_visible}
                  />
                </div>
              </div>
              <StockMatrixToolbar
                warehouses={allWarehouses}
                products={allProducts}
                productGroups={product_groups}
                hiddenWarehouseIds={hiddenWarehouseIds}
                onHiddenWarehouseIdsChange={setHiddenWarehouseIds}
                filters={filters}
                onFiltersChange={setFilters}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                actions={<FoldAllButtons onExpandAll={expandAll} onCollapseAll={collapseAll} />}
              />
            </>
          }
        >
          {/* As wide as the widest open market, so every band's background
              spans the full scroll width. */}
          <div style={{ width: "max-content", minWidth: "100%" }}>
            {sections.map((s) => {
              const open = market !== null || openMarketIds.has(s.market.id);
              return (
                <div key={s.market.id}>
                  {market === null && <GroupHeader
                    label={s.market.name}
                    market={s.market}
                    productCount={s.products.length}
                    summary={s.summary}
                    currency={s.market.currency}
                    valueVisible={value_visible}
                    open={open}
                    onToggle={() => setOpenMarketIds((prev) => toggled(prev, s.market.id))}
                  />}
                  {open &&
                    (s.market.warehouses.length === 0 ? (
                      <div className="px-6 py-6 text-sm">
                        No warehouses in {s.market.name} are mapped to an org node yet.
                      </div>
                    ) : (
                      <StockMatrixGrid
                        warehouses={s.warehouses}
                        products={s.products}
                        healthColumns={columns}
                        currency={s.market.currency}
                        valueVisible={value_visible}
                        groups={s.groups}
                        onSelectProduct={(p) => openProductQuickView(p.external_id, s.market)}
                        sidebarOpen={quickViewId !== null}
                        sort={sort}
                        onSortChange={setSort}
                        filtered={s.filtered}
                        closedGroupKeys={closedGroupKeys[s.market.id] ?? EMPTY}
                        onToggleGroup={(key) => toggleGroup(s.market.id, key)}
                      />
                    ))}
                </div>
              );
            })}
          </div>
        </MatrixScrollBox>
      </div>
    </AppLayout>
  );
}
